/* eslint-disable no-case-declarations */
import { z } from "zod/v4";

export const timeFrameSchema = z.union([
  z.literal("1m"),
  z.literal("3m"),
  z.literal("5m"),
  z.literal("15m"),
  z.literal("30m"),
  z.literal("1h"),
  z.literal("2h"),
  z.literal("4h"),
  z.literal("6h"),
  z.literal("8h"),
  z.literal("12h"),
  z.literal("1d"),
  z.literal("3d"),
  z.literal("1w"),
  z.literal("1M"),
]);

export const timeFrameToIntervalSchema = timeFrameSchema.transform(
  (timeFrame) => {
    const [time, unit] = timeFrame.split("");

    switch (unit) {
      case "m":
        return parseInt(time) * 60 * 1000;
      case "h":
        return parseInt(time) * 60 * 60 * 1000;
      case "d":
        return parseInt(time) * 24 * 60 * 60 * 1000;
      case "w":
        return parseInt(time) * 7 * 24 * 60 * 60 * 1000;
      case "M":
        return parseInt(time) * 30 * 24 * 60 * 60 * 1000;
      default:
        return 0;
    }
  },
);

export const exchangeInfoSchema = z.object({
  timezone: z.string(),
  serverTime: z.number(),
  rateLimits: z.array(
    z.object({
      rateLimitType: z.string(),
      interval: z.string(),
      intervalNum: z.number(),
      limit: z.number(),
    }),
  ),
  exchangeFilters: z.array(z.unknown()),
  symbols: z.array(
    z.object({
      symbol: z.string(),
      status: z.string(),
      baseAsset: z.string(),
      baseAssetPrecision: z.number(),
      quoteAsset: z.string(),
      quotePrecision: z.number(),
      quoteAssetPrecision: z.number(),
      baseCommissionPrecision: z.number(),
      quoteCommissionPrecision: z.number(),
      orderTypes: z.array(z.string()),
      icebergAllowed: z.boolean(),
      ocoAllowed: z.boolean(),
      quoteOrderQtyMarketAllowed: z.boolean(),
      isSpotTradingAllowed: z.boolean(),
      isMarginTradingAllowed: z.boolean(),
      filters: z.array(
        z.object({
          filterType: z.string(),
          minPrice: z.string().optional(),
          maxPrice: z.string().optional(),
          tickSize: z.string().optional(),
          multiplierUp: z.string().optional(),
          multiplierDown: z.string().optional(),
          avgPriceMins: z.number().optional(),
          minQty: z.string().optional(),
          maxQty: z.string().optional(),
          stepSize: z.string().optional(),
          minNotional: z.string().optional(),
          applyToMarket: z.boolean().optional(),
          limit: z.number().optional(),
          maxNumOrders: z.number().optional(),
          maxNumAlgoOrders: z.number().optional(),
        }),
      ),
      permissions: z.array(z.string()),
    }),
  ),
});

export const avgPriceSchema = z.object({
  mins: z.number(),
  price: z.string(),
});

export const tickerMiniSchema = z.object({
  symbol: z.string(),
  openPrice: z.string(),
  highPrice: z.string(),
  lowPrice: z.string(),
  lastPrice: z.string(),
  volume: z.string(),
  quoteVolume: z.string(),
  openTime: z.number(),
  closeTime: z.number(),
  firstId: z.number(),
  lastId: z.number(),
  count: z.number(),
});

export const tickerFullSchema = tickerMiniSchema.extend({
  priceChange: z.string(),
  priceChangePercent: z.string(),
  weightedAvgPrice: z.string(),
});

export const ticker24MiniSchema = z.object({
  symbol: z.string(),
  openPrice: z.string(),
  highPrice: z.string(),
  lowPrice: z.string(),
  lastPrice: z.string(),
  volume: z.string(),
  quoteVolume: z.string(),
  openTime: z.number(),
  closeTime: z.number(),
  firstId: z.number(),
  lastId: z.number(),
  count: z.number(),
});

export const ticker24FullSchema = ticker24MiniSchema.extend({
  priceChange: z.string(),
  priceChangePercent: z.string(),
  weightedAvgPrice: z.string(),
  prevClosePrice: z.string(),
  lastQty: z.string(),
  bidPrice: z.string(),
  bidQty: z.string(),
  askPrice: z.string(),
  askQty: z.string(),
});

export const tickerPrice = z.object({
  symbol: z.string(),
  price: z.string(),
});

export const klineDataSchema = z.object({
  openTime: z.number(),
  open: z.string(),
  high: z.string(),
  low: z.string(),
  close: z.string(),
  volume: z.string(),
  closeTime: z.number(),
  quoteVolume: z.string(),
  numberOfTrades: z.number(),
  takerBuyBaseAssetVolume: z.string(),
  takerBuyQuoteAssetVolume: z.string(),
});

export type TimeFrame = z.infer<typeof timeFrameSchema>;
export type ExchangeInfo = z.infer<typeof exchangeInfoSchema>;
export type AvgPrice = z.infer<typeof avgPriceSchema>;
export type TickerMini = z.infer<typeof tickerMiniSchema>;
export type TickerFull = z.infer<typeof tickerFullSchema>;
export type Ticker = TickerMini | TickerFull;
export type Ticker24Mini = z.infer<typeof ticker24MiniSchema>;
export type Ticker24Full = z.infer<typeof ticker24FullSchema>;
export type Ticker24 = Ticker24Mini | Ticker24Full;
export type TickerPrice = z.infer<typeof tickerPrice>;
export type KlineData = z.infer<typeof klineDataSchema>;

const parseRawKlineData = (data: (number | string)[]): KlineData => {
  return {
    openTime: data[0] as number,
    open: data[1] as string,
    high: data[2] as string,
    low: data[3] as string,
    close: data[4] as string,
    volume: data[5] as string,
    closeTime: data[6] as number,
    quoteVolume: data[7] as string,
    numberOfTrades: data[8] as number,
    takerBuyBaseAssetVolume: data[9] as string,
    takerBuyQuoteAssetVolume: data[10] as string,
  };
};

export const klineRawToKlineDataSchema = z
  .array(z.array(z.union([z.string(), z.number()])))
  .transform((data, ctx) => {
    const mapped = data.map((item) => parseRawKlineData(item));

    const schema = z.array(klineDataSchema);
    const result = schema.safeParse(mapped);

    if (!result.success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Error during kline raw to kline data conversion: ${result.error.toString()}`,
      });
      return z.NEVER;
    }

    return result.data;
  });

// ================== Websocket ==================

export const binanceWsPayloadSchema = z.object({
  stream: z.string(),
  data: z.unknown(),
});
export type BinanceWsPayload = z.infer<typeof binanceWsPayloadSchema>;

export const binanceWsConnectMessage = z.object({
  method: z.string(),
  params: z.array(z.string()),
  id: z.number(),
});

export type BinanceWsConnectMessage = z.infer<typeof binanceWsConnectMessage>;

const binanceWsKlineEvent = z.object({
  eventType: z.literal("kline"),
  eventTime: z.number(),
  symbol: z.string(),
  kline: z.object({
    startTime: z.number(),
    endTime: z.number(),
    symbol: z.string(),
    interval: z.string(),
    firstTradeId: z.number(),
    lastTradeId: z.number(),
    open: z.string(),
    close: z.string(),
    high: z.string(),
    low: z.string(),
    volume: z.string(),
    trades: z.number(),
    final: z.boolean(),
    quoteVolume: z.string(),
    buyVolume: z.string(),
    quoteBuyVolume: z.string(),
  }),
});

// const binanceMiniTickerEvent = z.object({
//   eventType: z.literal("24hrMiniTicker"),
//   eventTime: z.number(),
//   symbol: z.string(),
//   openPrice: z.string(),
//   highPrice: z.string(),
//   lowPrice: z.string(),
//   closePrice: z.string(),
//   totalTradedBaseAssetVolume: z.string(),
//   totalTradedQuoteAssetVolume: z.string(),
// });

export type BinanceWsKlineEvent = z.infer<typeof binanceWsKlineEvent>;

// const binanceWsAggTradeEvent = z.object({
//   eventType: z.literal("aggTrade"),
//   eventTime: z.number(),
//   symbol: z.string(),
//   aggTrade: z.object({
//     aggId: z.number(),
//     price: z.string(),
//     quantity: z.string(),
//     firstTradeId: z.number(),
//     lastTradeId: z.number(),
//     tradeTime: z.number(),
//     isBuyerMaker: z.boolean(),
//     isBestMatch: z.boolean(),
//   }),
// });

// type BinanceWsAggTradeEvent = z.infer<typeof binanceWsAggTradeEvent>;

// const binanceWsTradeEvent = z.object({
//   eventType: z.literal("trade"),
//   eventTime: z.number(),
//   symbol: z.string(),
//   trade: z.object({
//     id: z.number(),
//     price: z.string(),
//     quantity: z.string(),
//     time: z.number(),
//     isBuyerMaker: z.boolean(),
//     isBestMatch: z.boolean(),
//   }),
// });

// type BinanceWsTradeEvent = z.infer<typeof binanceWsTradeEvent>;

// const binanceWsDepthEvent = z.object({
//   eventType: z.literal("depth"),
//   eventTime: z.number(),
//   symbol: z.string(),
//   updateId: z.number(),
//   firstUpdateId: z.number(),
//   finalUpdateId: z.number(),
//   bidDepth: z.array(
//     z.object({
//       price: z.string(),
//       quantity: z.string(),
//     })
//   ),
//   askDepth: z.array(
//     z.object({
//       price: z.string(),
//       quantity: z.string(),
//     })
//   ),
// });

// type BinanceWsDepthEvent = z.infer<typeof binanceWsDepthEvent>;

export type BinanceWsStreamType =
  | "kline"
  | "aggTrade"
  | "trade"
  | "depth"
  | "miniTicker"
  | "ticker"
  | "allMiniTicker"
  | "allTicker";

type BinanceWsKlineStream = `${string}@kline_${string}`;
type BinanceWsAggTradeStream = `${string}@aggTrade`;
type BinanceWsTradeStream = `${string}@trade`;
type BinanceWsDepthStream = `${string}@depth`;
type BinanceWsMiniTickerStream = `${string}@miniTicker`;
type BinanceWsTickerStream = `${string}@ticker`;
type BinanceWsAllMiniTickerStream = "!miniTicker@arr";
type BinanceWsAllTickerStream = "!ticker@arr";

export type BinanceWsStream =
  | BinanceWsKlineStream
  | BinanceWsAggTradeStream
  | BinanceWsTradeStream
  | BinanceWsDepthStream
  | BinanceWsMiniTickerStream
  | BinanceWsTickerStream
  | BinanceWsAllMiniTickerStream
  | BinanceWsAllTickerStream;

const parseWsPayload = (payload: BinanceWsPayload) => {
  const { stream, data } = payload;

  const [symbol, type] = stream.split("@") as [string, BinanceWsStreamType];

  return { symbol, type, data };
};

const wsKlineResponse = z.object({
  e: z.string(), // Event type
  E: z.number(), // Event time
  s: z.string(), // Symbol
  k: z.object({
    // Kline data
    t: z.number(), // Kline start time
    T: z.number(), // Kline close time
    s: z.string(), // Symbol
    i: z.string(), // Interval
    f: z.number(), // First trade ID
    L: z.number(), // Last trade ID
    o: z.string(), // Open price
    c: z.string(), // Close price
    h: z.string(), // High price
    l: z.string(), // Low price
    v: z.string(), // Base asset volume
    n: z.number(), // Number of trades
    x: z.boolean(), // Is this kline closed?
    q: z.string(), // Quote asset volume
    V: z.string(), // Taker buy base asset volume
    Q: z.string(), // Taker buy quote asset volume
    B: z.string(), // Ignore
  }),
});

export type BinanceWsKlineResponse = z.infer<typeof wsKlineResponse>;

const wsKlineMiniTickerResponse = z.object({
  e: z.string(), // Event type
  E: z.number(), // Event time
  s: z.string(), // Symbol
  c: z.string(), // Close price
  o: z.string(), // Open price
  h: z.string(), // High price
  l: z.string(), // Low price
  v: z.string(), // Total traded base asset volume
  q: z.string(), // Total traded quote asset volume
});

export type BinanceWsKlineMiniTickerResponse = z.infer<
  typeof wsKlineMiniTickerResponse
>;

const wsTickerResponse = wsKlineMiniTickerResponse.extend({
  p: z.string(), // Price change
  P: z.string(), // Price change percent
  w: z.string(), // Weighted average price
  x: z.string(), // First trade(F)-1 price (first trade before the 24hr rolling window)
  Q: z.string(), // Last quantity
  b: z.string(), // Best bid price
  B: z.string(), // Best bid quantity
  a: z.string(), // Best ask price
  A: z.string(), // Best ask quantity
  O: z.number(), // Statistics open time
  C: z.number(), // Statistics close time
  F: z.number(), // First trade ID
  L: z.number(), // Last trade Id
  n: z.number(), // Total number of trades
});

export type BinanceWsTickerResponse = z.infer<typeof wsTickerResponse>;

const wsTradeResponse = z.object({
  e: z.string(), // Event type
  E: z.number(), // Event time
  s: z.string(), // Symbol
  t: z.number(), // Trade ID
  p: z.string(), // Price
  q: z.string(), // Quantity
  b: z.number(), // Buyer order ID
  a: z.number(), // Seller order ID
  T: z.number(), // Trade time
  m: z.boolean(), // Is the buyer the market maker?
  M: z.boolean(), // Ignore
});

export type BinanceWsTradeResponse = z.infer<typeof wsTradeResponse>;

export const parseWsResponse = (payload: BinanceWsPayload) => {
  // eslint-disable-next-line no-unused-vars
  const { symbol, type, data } = parseWsPayload(payload);

  switch (true) {
    case type.includes("kline"):
      const parsedEvent = wsKlineResponse.safeParse(data);
      return parsedEvent.success
        ? (parsedEvent.data as BinanceWsKlineResponse)
        : null;
    case type === "aggTrade":
    case type === "trade":
      const parsedAggTradeEvent = wsTradeResponse.safeParse(data);
      return parsedAggTradeEvent.success
        ? (parsedAggTradeEvent.data as BinanceWsTradeResponse)
        : null;

    case type === "depth":
      return null;
    case type === "ticker":
      const parsedTickerEvent = wsTickerResponse.safeParse(data);
      return parsedTickerEvent.success
        ? (parsedTickerEvent.data as BinanceWsTickerResponse)
        : null;
    case type === "miniTicker":
      const parsedMiniTickerEvent = wsKlineMiniTickerResponse.safeParse(data);
      return parsedMiniTickerEvent.success
        ? (parsedMiniTickerEvent.data as BinanceWsKlineMiniTickerResponse)
        : null;
    default:
      return null;
  }
};
